import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, GitBranch, Loader2, PauseCircle, PlayCircle, RefreshCw, Search, Undo2, Wrench, XCircle } from "lucide-react";
import { EmptyState } from "./common";
import { FixRowsDialog } from "./FixRowsDialog";
import { PaginationBar, TableRowsSkeleton, useDebounced, usePagedList } from "./PaginationBar";
import { UndoPaymentBatchDialog } from "./UndoPaymentBatchDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Checkbox } from "./ui/checkbox";
import { BatchLogSelectionBar, DeleteBatchLogsDialog } from "./DeleteBatchLogsDialog";
import { api, downloadBlob, errMsg, fmtDateTime, fmtINR } from "../lib/api";
import { deleteBatchLogs, useBatchLogAccess } from "../lib/batchLogs";

/**
 * Batch History — every applied bulk payment import for one scope (main register / Third Party).
 * Per batch: uploaded when & by whom, total / imported / failed (+ skipped) and two Excel exports:
 *   • Imported records (with the system receipt numbers)
 *   • Failed records — template layout + IMPORT STATUS + REASON, so admins fix the file and upload it straight back.
 * Phase 107: "Fix & re-import" (editable grid → wizard preview via `onRetryPreview`) and "Undo" (roll the batch back).
 */
export function PaymentImportBatchHistory({ code, scope = "main", testId, onRetryPreview, readOnly = false }) {
  const tid = testId || (scope === "third_party" ? "tp-payment-batches" : "payment-batches");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});     // `${id}:${kind}` → true while downloading
  const [fixFor, setFixFor] = useState(null);
  const [undoFor, setUndoFor] = useState(null);
  const [resuming, setResuming] = useState(null);
  const search = useDebounced(q);
  const params = useMemo(() => ({ scope, ...(search ? { search } : {}) }), [scope, search]);
  const list = usePagedList(`/t/${code}/payment-import/batches`, { params, initialLimit: 25 });
  const totals = list.data?.totals;
  const active = list.data?.active || 0;

  // Phase 151: remove batch records from this history (log entry only — payments stay). Super Admin, or a
  // Municipal Admin the Super Admin allowed.
  const { canDelete: canDeleteLogs } = useBatchLogAccess(code);
  const canDelete = canDeleteLogs && !readOnly;   // Phase 152: "View only" never deletes batch records
  const [selected, setSelected] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [list.page, list.limit, scope, search]);
  const selectable = list.items.filter((b) => b.log_deletable);
  const allSelected = selectable.length > 0 && selectable.every((b) => selected.has(b.id));
  const someSelected = selectable.some((b) => selected.has(b.id));
  const toggleAll = (on) => setSelected((prev) => { const n = new Set(prev); selectable.forEach((b) => { if (on) n.add(b.id); else n.delete(b.id); }); return n; });
  const toggleOne = (id, on) => setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const selectedItems = [...selected].map((id) => ({ kind: "payment_import", id, filename: list.items.find((b) => b.id === id)?.filename }));

  // Phase 108: while any batch on this page is still processing, refresh every 2s so progress bars move
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => list.reload(), 2000);
    return () => clearInterval(t);
  }, [active, list.reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const resumeBatch = async (b) => {
    setResuming(b.id);
    try {
      const r = await api.post(`/t/${code}/payment-import/${b.id}/resume`);
      toast.success("Import resumed", { description: r.data.message });
      list.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResuming(null);
    }
  };

  const exportBatch = async (b, kind) => {
    const key = `${b.id}:${kind}`;
    setBusy((s) => ({ ...s, [key]: true }));
    try {
      await downloadBlob(`/t/${code}/payment-import/${b.id}/export?kind=${kind}`, `${b.filename || "payment-import"}_${kind}.xlsx`);
      const n = kind === "imported" ? b.counts?.imported_rows
        : kind === "advance" ? b.counts?.advance_rows
        : (b.counts?.failed_rows || 0) + (b.counts?.skipped_rows || 0);
      toast.success(kind === "imported" ? "Imported records exported" : kind === "advance" ? "Advance credit records exported" : "Failed records exported",
        { description: kind === "advance"
          ? `${n ?? 0} payment${n === 1 ? "" : "s"} exceeded the demand · ${fmtINR(b.counts?.advance || 0)} advance · ${b.filename}`
          : `${n ?? 0} row${n === 1 ? "" : "s"} · ${b.filename}` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  // Phase 121: one Excel per summary card, across every batch of this scope (and the current search)
  const exportAll = async (kind, label, count) => {
    const key = `all:${kind}`;
    setBusy((s) => ({ ...s, [key]: true }));
    try {
      const qs = new URLSearchParams({ kind, scope, ...(search ? { search } : {}) }).toString();
      await downloadBlob(`/t/${code}/payment-import/batches/export?${qs}`,
        `${scope === "third_party" ? "third-party" : "main"}_batch-history_${kind}.xlsx`);
      toast.success(`${label} exported`, {
        description: `${Number(count || 0).toLocaleString("en-IN")} ${kind === "batches" ? "batch" : "row"}${count === 1 ? "" : kind === "batches" ? "es" : "s"} across all batches${search ? ` matching “${search}”` : ""}`,
      });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const SUMMARY_EXPORT_TIPS = {
    batches: "Download all batches as Excel (one row per batch)",
    rows: "Download every uploaded row across all batches as Excel",
    imported: "Download all successfully imported records across all batches as Excel (rolled-back batches excluded)",
    failed: "Download all failed rows across all batches as Excel (rejected in preview + failed while importing)",
  };

  return (
    <div className="space-y-4" data-testid={tid}>
      <TooltipProvider delayDuration={200}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`${tid}-totals`}>
        {[
          ["Batches", totals?.batches, `${tid}-total-batches`, "",
            totals?.processing_batches > 0 ? `${totals.processing_batches} processing now` : totals?.undone_batches > 0 ? `${totals.undone_batches} rolled back` : null, "batches"],
          ["Rows uploaded", totals?.total_rows, `${tid}-total-rows`, "", null, "rows"],
          ["Successfully imported", totals?.net_imported_rows ?? totals?.imported_rows, `${tid}-total-imported`, "text-[hsl(var(--success))]",
            totals?.reversed_rows > 0 ? `${totals.reversed_rows} reversed by undo` : null, "imported"],
          ["Failed", totals?.failed_rows, `${tid}-total-failed`, totals?.failed_rows > 0 ? "text-destructive" : "", null, "failed"],
        ].map(([l, v, t, cls, sub, kind]) => {
          const downloading = !!busy[`all:${kind}`];
          const canExport = Number(v) > 0 && !downloading && !list.loading;
          return (
            <Card key={l} className="shadow-sm"><CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">{l}</p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* span wrapper keeps the tooltip reachable while the button is disabled */}
                    <span className="inline-flex -mt-1 -mr-1" tabIndex={canExport ? -1 : 0}>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary shrink-0"
                        onClick={() => exportAll(kind, l, v)} disabled={!canExport} aria-label={`Download all ${l.toLowerCase()} as Excel`}
                        data-testid={`${tid}-download-${kind}`}>
                        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs max-w-[240px]">
                    {Number(v) > 0 ? SUMMARY_EXPORT_TIPS[kind] : "Nothing to download yet"}
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className={`text-xl font-semibold tabular-nums font-heading ${cls}`} data-testid={t}>{v == null ? "—" : Number(v).toLocaleString("en-IN")}</p>
              {sub && <p className="text-[10px] text-muted-foreground" data-testid={`${t}-sub`}>{sub}</p>}
            </CardContent></Card>
          );
        })}
      </div>
      </TooltipProvider>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 h-9" placeholder="Search file name or uploaded by…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${tid}-search-input`} />
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => list.reload()} disabled={list.loading} data-testid={`${tid}-refresh-button`}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {canDelete && (
          <BatchLogSelectionBar count={selected.size} onClear={() => setSelected(new Set())} onDelete={() => setDeleteOpen(true)}
            disabled={list.loading} testId={tid} />
        )}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid={`${tid}-error`}>
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Could not load the batch history.</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid={`${tid}-error-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
            </div>
          ) : !list.data ? (
            <TableRowsSkeleton rows={6} cols={7} testId={`${tid}-skeleton`} />
          ) : list.items.length === 0 ? (
            <EmptyState title={q ? "No batches match" : "No payment imports yet"}
              subtitle={q ? "Try a different file name or staff name." : `Applied bulk payment imports for ${scope === "third_party" ? "Third Party entries" : "the main register"} will appear here.`} />
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className="overflow-x-auto">
                <Table data-testid={`${tid}-table`}>
                  <TableHeader>
                    <TableRow>
                      {canDelete && (
                        <TableHead className="w-10">
                          <Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false} onCheckedChange={(on) => toggleAll(on === true)}
                            disabled={selectable.length === 0} aria-label="Select all batches on this page" data-testid={`${tid}-select-all`} />
                        </TableHead>
                      )}
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Uploaded by</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead className="text-right">Total rows</TableHead>
                      <TableHead className="text-right">Imported</TableHead>
                      <TableHead className="text-right">Failed</TableHead>
                      <TableHead className="text-right hidden lg:table-cell">Amount</TableHead>
                      <TableHead className="text-right">Export</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((b) => {
                      const c = b.counts || {};
                      const failedAll = (c.failed_rows || 0);
                      const fixable = failedAll + (c.skipped_rows || 0);
                      const undone = b.undo?.status === "completed";
                      const undoing = b.undo?.status === "processing";
                      const running = b.status === "processing" && !b.stalled;
                      const stalled = b.status === "processing" && b.stalled;
                      const stopped = b.status === "failed";
                      const inFlight = running || stalled || stopped;
                      const pct = c.to_write ? Math.min(100, Math.round(((c.processed || 0) / c.to_write) * 100)) : 0;
                      return (
                        <TableRow key={b.id} className={selected.has(b.id) ? "bg-primary/5" : undone ? "bg-muted/30" : running ? "bg-primary/[0.03]" : ""}
                          data-state={selected.has(b.id) ? "selected" : undefined} data-testid={`${tid}-row-${b.id}`}>
                          {canDelete && (
                            <TableCell className="w-10 align-top pt-3.5">
                              <Checkbox checked={selected.has(b.id)} onCheckedChange={(on) => toggleOne(b.id, on === true)} disabled={!b.log_deletable}
                                aria-label={`Select batch ${b.filename || b.id}`}
                                title={b.log_deletable ? undefined : (b.log_delete_reason === "undo_running" ? "Rollback is running" : "Still processing — cannot be removed yet")}
                                data-testid={`${tid}-select-${b.id}`} />
                            </TableCell>
                          )}
                          <TableCell className="whitespace-nowrap align-top">
                            <p className="text-sm tabular-nums" data-testid={`${tid}-row-uploaded-${b.id}`}>{fmtDateTime(b.created_at)}</p>
                            {b.applied_at && b.applied_at.slice(0, 16) !== (b.created_at || "").slice(0, 16) && (
                              <p className="text-[11px] text-muted-foreground">applied {fmtDateTime(b.applied_at)}</p>
                            )}
                            {inFlight && b.apply_started_at && <p className="text-[11px] text-muted-foreground">started {fmtDateTime(b.apply_started_at)}</p>}
                          </TableCell>
                          <TableCell className="align-top text-sm" data-testid={`${tid}-row-by-${b.id}`}>{b.uploaded_by_name || "—"}</TableCell>
                          <TableCell className="align-top">
                            <p className="text-sm truncate max-w-[220px] flex items-center gap-1.5" title={b.filename}>
                              <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> <span className="truncate" data-testid={`${tid}-row-file-${b.id}`}>{b.filename}</span>
                            </p>
                            <div className="flex items-center gap-1 flex-wrap mt-1">
                              <Badge variant="outline" className="text-[10px] font-normal">{b.scope === "third_party" ? "Third Party" : "Main register"}</Badge>
                              {running && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-primary/10 text-primary border-primary/20 gap-1" data-testid={`${tid}-row-processing-${b.id}`}>
                                  <Loader2 className="h-3 w-3 animate-spin" /> Processing…
                                </Badge>
                              )}
                              {stalled && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40 gap-1" data-testid={`${tid}-row-stalled-${b.id}`}>
                                  <PauseCircle className="h-3 w-3" /> Stopped — no heartbeat
                                </Badge>
                              )}
                              {stopped && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-destructive/10 text-destructive border-destructive/20 gap-1" title={b.progress?.error || undefined} data-testid={`${tid}-row-stopped-${b.id}`}>
                                  <XCircle className="h-3 w-3" /> Stopped part-way
                                </Badge>
                              )}
                              {b.parent_import_id && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-primary/5 text-primary border-primary/20 gap-1" title={`Fix of ${b.parent_filename || "an earlier batch"}`} data-testid={`${tid}-row-fix-of-${b.id}`}>
                                  <GitBranch className="h-3 w-3" /> Fix batch
                                </Badge>
                              )}
                              {b.retry_import_ids?.length > 0 && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20" data-testid={`${tid}-row-fixed-${b.id}`}>
                                  {b.retry_import_ids.length} fix batch{b.retry_import_ids.length === 1 ? "" : "es"}
                                </Badge>
                              )}
                              {undone && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-destructive/10 text-destructive border-destructive/20 gap-1" title={b.undo?.reason ? `Reason: ${b.undo.reason}` : undefined} data-testid={`${tid}-row-undone-${b.id}`}>
                                  <Undo2 className="h-3 w-3" /> Rolled back
                                </Badge>
                              )}
                              {undoing && <Badge variant="outline" className="text-[10px] font-normal animate-pulse" data-testid={`${tid}-row-undoing-${b.id}`}>Rolling back…</Badge>}
                            </div>
                            {inFlight && (
                              <div className="mt-2 max-w-[260px] space-y-1" role="status" aria-live="polite" data-testid={`${tid}-row-progress-${b.id}`}>
                                <Progress value={pct} className={`h-1.5 ${stopped || stalled ? "[&>div]:bg-[hsl(var(--warning))]" : ""}`} />
                                <p className="text-[10px] text-muted-foreground tabular-nums" data-testid={`${tid}-row-progress-text-${b.id}`}>
                                  {(c.processed || 0).toLocaleString("en-IN")} / {(c.to_write || 0).toLocaleString("en-IN")} rows · {pct}%
                                  {b.progress?.chunk_size ? ` · batches of ${b.progress.chunk_size}` : ""}
                                </p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums align-top" data-testid={`${tid}-row-total-${b.id}`}>{c.total_rows ?? b.total_rows ?? 0}</TableCell>
                          <TableCell className="text-right tabular-nums align-top">
                            <span className={`inline-flex items-center gap-1 font-medium ${undone ? "text-muted-foreground line-through" : "text-[hsl(var(--success))]"}`} data-testid={`${tid}-row-imported-${b.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" /> {c.imported_rows ?? 0}
                            </span>
                            {inFlight && <p className="text-[10px] text-muted-foreground">so far</p>}
                            {!inFlight && (b.summary?.replaced_rows > 0) && (
                              <p className={`text-[10px] ${undone ? "text-muted-foreground" : "text-amber-800"}`} data-testid={`${tid}-row-replaced-${b.id}`}>
                                {b.summary.replaced_rows} replaced existing{undone ? " (restored)" : ""}
                              </p>
                            )}
                            {!inFlight && (b.summary?.review_replaced_receipts > 0) && (   /* Phase 194 */
                              <p className={`text-[10px] ${undone ? "text-muted-foreground" : "text-amber-800"}`} data-testid={`${tid}-row-review-replaced-${b.id}`}>
                                {b.summary.review_replaced_receipts} manual receipt{b.summary.review_replaced_receipts === 1 ? "" : "s"} replaced{undone ? " (restored)" : ""}
                              </p>
                            )}
                            {!inFlight && (b.summary?.review_skipped_rows > 0) && (
                              <p className={`text-[10px] ${undone ? "text-muted-foreground" : "text-indigo-800"}`} data-testid={`${tid}-row-review-skipped-${b.id}`}>
                                {b.summary.review_skipped_rows} skipped → Flagged Payments{undone ? " (void)" : ""}
                              </p>
                            )}
                            {undone && <p className="text-[10px] text-destructive" data-testid={`${tid}-row-reversed-${b.id}`}>{b.undo.processed} reversed</p>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums align-top">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`inline-flex items-center gap-1 font-medium ${failedAll > 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid={`${tid}-row-failed-${b.id}`}>
                                  <XCircle className="h-3.5 w-3.5" /> {failedAll}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="text-xs">
                                {c.rejected_rows || 0} rejected in preview · {c.write_failed_rows || 0} failed while writing
                                {c.skipped_rows ? ` · ${c.skipped_rows} un-ticked` : ""}
                              </TooltipContent>
                            </Tooltip>
                            {c.skipped_rows > 0 && <p className="text-[10px] text-muted-foreground" data-testid={`${tid}-row-skipped-${b.id}`}>+{c.skipped_rows} skipped</p>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums align-top hidden lg:table-cell">
                            <span className={undone ? "line-through text-muted-foreground" : ""}>{fmtINR(c.amount || 0)}</span>
                            {c.advance > 0 && (
                              <p className="text-[10px] text-muted-foreground flex items-center justify-end gap-0.5 whitespace-nowrap" data-testid={`${tid}-row-advance-${b.id}`}>
                                incl. <span className="font-medium text-amber-800">{fmtINR(c.advance)}</span> advance
                                {/* Phase 150: Excel of the payments that exceeded the demand (PIN, owner, demand, payment, advance) */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 ml-0.5 text-amber-800 hover:text-amber-900 hover:bg-amber-50"
                                      onClick={() => exportBatch(b, "advance")} disabled={!!busy[`${b.id}:advance`] || inFlight}
                                      aria-label="Download advance credit records as Excel" data-testid={`${tid}-export-advance-${b.id}`}>
                                      {busy[`${b.id}:advance`] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs max-w-[260px]">
                                    Download Excel of the {c.advance_rows ? `${c.advance_rows} ` : ""}payment{c.advance_rows === 1 ? "" : "s"} in this batch that exceeded the
                                    demand — PIN, owner, demand amount, payment amount and the advance credit created{undone ? " (batch rolled back)" : ""}.
                                  </TooltipContent>
                                </Tooltip>
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                              {(stalled || stopped) && (
                                <Button size="sm" className="h-7 text-xs" onClick={() => resumeBatch(b)} disabled={resuming === b.id} data-testid={`${tid}-resume-${b.id}`}>
                                  <PlayCircle className="h-3 w-3 mr-1" /> {resuming === b.id ? "Resuming…" : `Resume (${Math.max(0, (c.to_write || 0) - (c.processed || 0))} left)`}
                                </Button>
                              )}
                              {running && <span className="text-xs text-muted-foreground" data-testid={`${tid}-row-running-note-${b.id}`}>Exports available when done</span>}
                              {!inFlight && (
                                <>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => exportBatch(b, "imported")}
                                disabled={!!busy[`${b.id}:imported`] || !(c.imported_rows > 0)} data-testid={`${tid}-export-imported-${b.id}`}>
                                <Download className="h-3 w-3 mr-1" /> Imported ({c.imported_rows ?? 0})
                              </Button>
                              <Button size="sm" variant="outline" className={`h-7 text-xs ${fixable > 0 ? "border-destructive/40 text-destructive hover:bg-destructive/5" : ""}`}
                                onClick={() => exportBatch(b, "failed")} disabled={!!busy[`${b.id}:failed`] || !(fixable > 0)}
                                data-testid={`${tid}-export-failed-${b.id}`}>
                                <Download className="h-3 w-3 mr-1" /> Failed ({fixable})
                              </Button>
                              {fixable > 0 && onRetryPreview && (
                                <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => setFixFor(b)} data-testid={`${tid}-fix-${b.id}`}>
                                  <Wrench className="h-3 w-3 mr-1" /> Fix & re-import
                                </Button>
                              )}
                              {c.imported_rows > 0 && !readOnly && (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:bg-destructive/5" onClick={() => setUndoFor(b)}
                                  disabled={undone || undoing} title={undone ? "Already rolled back" : "Roll back every payment from this batch"} data-testid={`${tid}-undo-${b.id}`}>
                                  <Undo2 className="h-3 w-3 mr-1" /> {undone ? "Undone" : "Undo"}
                                </Button>
                              )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId={`${tid}-pagination`} />
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>
      <p className="text-[11px] text-muted-foreground" data-testid={`${tid}-note`}>
        The <b>Failed</b> file keeps the Import Payments template columns plus IMPORT STATUS and REASON — correct the rows and upload the same file again,
        or use <b>Fix & re-import</b> to correct them right here. Rows you un-ticked in the preview are included as “Skipped (not ticked)”.
        <b> Undo</b> moves every payment of a batch to the recycle bin and restores the demand balances.
        Where a batch shows <b>advance</b>, the small download icon beside it lists every property whose payment exceeded the demand
        (PIN, owner, demand, payment, advance credit).
      </p>

      <FixRowsDialog code={code} batch={fixFor} open={!!fixFor} onOpenChange={(v) => !v && setFixFor(null)}
        onPreview={(p) => onRetryPreview?.(p, fixFor)} testId={`${tid}-fix`} />
      <UndoPaymentBatchDialog code={code} batch={undoFor} open={!!undoFor} onOpenChange={(v) => !v && setUndoFor(null)}
        onDone={() => list.reload()} testId={`${tid}-undo`} />
      {canDelete && (
        <DeleteBatchLogsDialog open={deleteOpen} onOpenChange={setDeleteOpen} items={selectedItems}
          request={(its) => deleteBatchLogs(code, its)} onDone={() => { setSelected(new Set()); list.reload(); }} testId={`${tid}-delete`} />
      )}
    </div>
  );
}

export default PaymentImportBatchHistory;
